import { Widget } from '../Widget';
import { Log } from '../../AuxillaryService/Log';
import { AnnouncementBase } from './Announcements';
import { AnnouncementController } from './AnnouncementController';
import {
    AnnouncementWidgetTimelineSlot,
    AnnouncementWidgetConfigurationDetails,
    AnnouncementWidgetConfiguration,
    AnnouncementTimelineSlot,
    UnknownTimelineSlot,
    ZodAnnouncementWidgetConfigurationDetails,
} from './types';
import { FadeInCommand, FadeOutCommand, TimelineSlot, WidgetState, WidgetType } from '../types';
import { ITimeline } from '../../Timeline';
import { getZoneById } from '../../Display';
import { pushReceiver } from '../../MessagingClient/instance';
import { announcementContainerTemplate } from './Templates';

class AnnouncementWidget extends Widget {
    type: WidgetType.ANNOUNCEMENT = WidgetType.ANNOUNCEMENT;
    declare config: AnnouncementWidgetConfigurationDetails;

    #controller?: AnnouncementController;

    constructor(widgetConfiguration: AnnouncementWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
    }

    protected validateConfigurationDetails(
        maybeAnnouncementWidgetConfigurationDetails: AnnouncementWidgetConfigurationDetails
    ): AnnouncementWidgetConfigurationDetails {
        return ZodAnnouncementWidgetConfigurationDetails.parse(
            maybeAnnouncementWidgetConfigurationDetails
        );
    }

    /**
     * Returns the announcement controller or throws if the controller has not
     * been defined.
     */
    private get safeController(): AnnouncementController {
        if (!this.#controller) {
            throw new Error(
                `invalid state: announcement widget ${this.id} has an undefined controller`
            );
        }

        return this.#controller;
    }

    /**
     * initializes widget and creates announcement controller instance
     */
    async initialize(): Promise<void> {
        try {
            await super.initialize();

            this._addUpdateListener();
            this._subscribeToAnnouncementMessage().then(); //do not await this

            const parentZone = getZoneById(this.zoneId);
            if (!parentZone) {
                throw new Error(`Unable to find a parent zone ${this.zoneId}`);
            }
            this.#controller = new AnnouncementController(this, parentZone.returnZoneInfo());

            this.wrapperElement.innerHTML = announcementContainerTemplate(null);

            try {
                await this.#controller.initialize();
            } catch (error) {
                this.onInitializeFailed(error);

                throw error;
            }

            this.#controller.setWidgetScale();
        } catch (error) {
            Log.Error(
                `Announcement Application Failed to initialize for widget: ${this.id}`,
                null,
                __fileline,
                error
            );

            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    async _doPause(currentSlot: AnnouncementTimelineSlot): Promise<void> {
        await currentSlot.params.stopPage();
    }

    /**
     * @inheritdoc
     */
    async getReadyForDisplay(): Promise<void> {
        if (this.wrapperElement.childElementCount < 1) {
            this.wrapperElement.innerHTML = announcementContainerTemplate(null);

            try {
                await this.safeController.initialize();

                this.safeController.setWidgetScale();
            } catch (reason) {
                this.onInitializeFailed(reason);

                throw reason;
            }
        }
    }

    /**
     * @inheritdoc
     */
    async play(currentSlot: AnnouncementTimelineSlot): Promise<void> {
        await super.play(currentSlot);
        // just need to play videos when resuming
        await currentSlot.params.runPage();
    }

    /**
     * @inheritdoc
     */
    async stop(): Promise<void> {
        await this.#controller?.deprovision();
        await super.stop();
    }

    /**
     * @todo document this
     */
    onInitializeFailed(error: unknown): void {
        this.state = WidgetState.STOPPED;

        // todo initialize has failed (set degraded)
        Log.Error(
            `Announcement Application Failed to initialize for widget: ${this.id}`,
            null,
            __fileline,
            error
        );
    }

    /**
     * determines slots to send to timeline
     */
    querySlots(numSlots: number, maxDuration: number): AnnouncementWidgetTimelineSlot[] {
        const slots = this.safeController.getSlots(numSlots, maxDuration);
        this.hasContentToPlay = slots.length !== 0;
        return slots;
    }

    /**
     * gets all slots to send to the timeline
     */
    querySlotsLinear(timeBased: boolean): AnnouncementWidgetTimelineSlot[] {
        const slots = this.safeController.getSlotsLinear(timeBased, this.minimumLoopTime);
        this.hasContentToPlay = slots.length !== 0;
        return slots;
    }

    /**
     * @inheritDoc
     * @todo return {@link FadeInCommand} to remove the need to call {@link Timeline.abortLoop} in
     * this function. Especially since the {@link Zone} code is designed to handle that.
     */
    async beforeFadeIn(
        currentSlot: AnnouncementTimelineSlot,
        previousSlot: UnknownTimelineSlot,
        timeline: ITimeline
    ): Promise<void> {
        this.safeController.lastOrderedAnnouncement(currentSlot.params);

        //we don't do this if w and prevSlot are the same
        await currentSlot.params.showAnnouncement();

        if (currentSlot.params.firstRun) {
            currentSlot.params.prepPage();
            currentSlot.params.firstRun = false;
        }

        if (
            currentSlot &&
            previousSlot &&
            currentSlot.params instanceof AnnouncementBase &&
            previousSlot.params instanceof AnnouncementBase &&
            currentSlot.params.configuration.id === previousSlot.params.configuration.id
        ) {
            const announcements = this.safeController.announcements();
            if (announcements && announcements.isChanged) {
                announcements.resetChanged();
                timeline.abortLoop();
            }
        } else {
            currentSlot.params.prepPage();
        }
    }

    /**
     * @inheritDoc
     */
    async afterFadeIn(): Promise<Partial<FadeInCommand> | void> {
        const abort = this.safeController.announcements()?.isChanged;
        if (abort) {
            this.safeController.purgeLineup();
        }

        this.safeController.announcements()?.resetChanged();

        return {
            abortLoop: abort,
        };
    }

    /**
     * @inheritDoc
     */
    async beforeFadeOut(
        currentSlot: AnnouncementTimelineSlot,
        nextSlot: UnknownTimelineSlot
    ): Promise<Partial<FadeOutCommand> | void> {
        //cancel fade in/fade out if next to play is the same announcement from the
        //same widget
        const cancelFade =
            nextSlot &&
            nextSlot.params instanceof AnnouncementBase &&
            nextSlot.widget.id === currentSlot.widget.id &&
            nextSlot.params.configuration.id === currentSlot.params.configuration.id;

        //If only there is only one video announcement, fading will trigger a video
        //restart.
        if (
            cancelFade &&
            (currentSlot.params.configuration.template === 'video' ||
                currentSlot.params.configuration.template === 'youtube')
        ) {
            return {
                cancelFade: false,
            };
        }

        return {
            cancelFade: cancelFade,
        };
    }

    /**
     * @inheritDoc
     */
    async afterFadeOut(
        currentSlot: AnnouncementTimelineSlot,
        nextSlot: TimelineSlot
    ): Promise<void> {
        let nextIsFullscreenAnnouncement = false;
        try {
            //knowing if the next announcement is fullscreen helps determine if
            //fullscreen css should be removed
            nextIsFullscreenAnnouncement =
                nextSlot &&
                nextSlot.widget &&
                nextSlot.widget.type === WidgetType.ANNOUNCEMENT &&
                (nextSlot as AnnouncementTimelineSlot).params.configuration.go_full_screen ===
                    'true';
        } catch (error) {
            if (error instanceof TypeError) {
                // This `TypeError` can happen when `nextSlot` is `undefined`,
                // which shouldn't happen.  Somewhere up the chain `nextSlot` is
                // not being properly set, and that's were the problem should be
                // fixed.  Unfortunately fixing that seems to be a bigger
                // project so for now we get this `try/catch`.
                Log.Error(
                    'Received bad data, cannot determine if next announcement is fullscreen.' +
                        ' Error message: ' +
                        String(error)
                );
                nextIsFullscreenAnnouncement = false;
            } else {
                throw error;
            }
        }

        await currentSlot.params.stopPage();

        //dis-engage the announcement
        await currentSlot.params.hideAnnouncement(nextIsFullscreenAnnouncement);
    }

    /**
     * @inheritDoc
     */
    async contentOnDeck(nextSlot: AnnouncementTimelineSlot): Promise<void> {
        await nextSlot.params.onDeck();
        await super.contentOnDeck(nextSlot);
    }

    /**
     * listener waits for message to run update actions
     */
    _addUpdateListener(): void {
        //announcement widget is used for both legacy announcements and new
        //media library/playlist. Central system sends different kinds of push
        //notifications for these different systems, so we must conditionally
        //listen based on whether we are legacy or ML/Playlist.
        const eventName = this.config.is_new_media_library
            ? 'playlist-update'
            : 'announcement-update';

        // add real-time message listener.  listener waits for message to run
        // update actions
        pushReceiver().addListener(eventName, async (event) => {
            const notificationSourceId =
                'playlistId' in event.detail ? event.detail.playlistId : event.detail.screenid;

            if (notificationSourceId === this.config.screenid) {
                Log.Info(
                    `Received ${eventName} push notification from MessagingClient for source id ${notificationSourceId}`,
                    null,
                    __fileline
                );

                this.safeController.announcementGroup?.processUpdateActions().catch(() => {
                    Log.Info('Failed to Process Update Actions', null, __fileline);
                });
            }
        });
    }

    /**
     * subscribes to messaging client
     */
    async _subscribeToAnnouncementMessage(): Promise<void> {
        // subscribe to real-time message channel
        try {
            await pushReceiver().subscribeToAnnouncement(this.config.screenid);

            Log.Info(
                `Announcement Widget Subscribed. widgetsid=${this.id}, screen_id=${this.config.screenid}`,
                null,
                __fileline
            );
        } catch (error) {
            Log.Error(
                'Failed to subscribe to announcement channel ' + this.config.screenid,
                null,
                __fileline,
                error
            );
        }
    }
}

export { AnnouncementWidget };
